// src/hooks/useBranches.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export const useBranches = (tenantId, options = {}) => {
  return useQuery({
    queryKey: ['branches', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
    ...options
  });
};

export const useBranch = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['branches', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .eq('id', branchId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!branchId,
    ...options
  });
};

/** Fetch branches by IDs (e.g. for context switcher when branch_admin has multiple branches) */
export const useBranchesByIds = (branchIds, options = {}) => {
  const ids = Array.isArray(branchIds) && branchIds.length > 0 ? branchIds : [];
  return useQuery({
    queryKey: ['branches', 'by-ids', ids],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .in('id', ids)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: ids.length > 0,
    ...options
  });
};

export const useCreateBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (branchData) => {
      const { data, error } = await supabase
        .from('branches')
        .insert(branchData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Branch created!');
    },
    onError: (error) => {
      toast.error(`Failed: ${error.message}`);
    }
  });
};

export const useUpdateBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('branches')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Ustawienia zapisane');
    },
    onError: (error) => {
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

export const useDeleteBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (branchId) => {
      // Get the session token for authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Not authenticated');
      }

      // Call the delete-branch Edge Function
      const { data, error } = await supabase.functions.invoke('delete-branch', {
        body: { branch_id: branchId }
      });

      if (error) throw error;

      // Check if the function returned an error
      if (data?.error) {
        throw new Error(data.error);
      }

      // Check if deletion was successful
      if (!data?.success) {
        throw new Error(data?.message || 'Failed to delete branch');
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      
      const summary = data?.summary || {};
      const itemsDeleted = Object.values(summary).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
      
      toast.success(`Branch deleted successfully! (${itemsDeleted} items removed)`);
    },
    onError: (error) => {
      toast.error(`Failed to delete branch: ${error.message}`);
    }
  });
};
